import { useState, useEffect, useCallback } from 'react';
import { friendApi, type FriendEntry, type FriendsList } from '../../auth/authApi';
import { useTranslation } from '../../i18n/useTranslation';
import styles from './UserPage.module.css';

// ─────────────────────────────────────────────────────────────────────────────

const CLASS_ICON: Record<string, string> = {
  warrior: '⚔',
  mage:    '🔮',
  rogue:   '🗡',
  archer:  '🏹',
  cleric:  '✨',
};

// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  token: string;
}

export default function FriendsSection({ token }: Props) {
  const [data,    setData]    = useState<FriendsList | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  // Search / add state
  const [query,   setQuery]   = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Per-entry action spinners  { [friendship_id]: boolean }
  const [acting, setActing] = useState<Record<string, boolean>>({});

  // ── Data loading ────────────────────────────────────────────────────────────

  const reload = useCallback(() => {
    setLoading(true);
    friendApi.list(token)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q || sending) return;
    setSending(true);
    setSendMsg(null);
    try {
      const res = await friendApi.request(token, q);
      setSendMsg({
        type: 'ok',
        text: res.status === 'accepted'
          ? t('friends.autoAccept')
          : t('friends.sentSuccess'),
      });
      setQuery('');
      reload();
    } catch (err) {
      setSendMsg({ type: 'err', text: (err as Error).message });
    } finally {
      setSending(false);
    }
  };

  /** Wraps an async action with per-row loading state then reloads the list. */
  const act = async (id: string, fn: () => Promise<unknown>) => {
    setActing(prev => ({ ...prev, [id]: true }));
    try { await fn(); reload(); }
    catch { /* row already gives visual feedback via disabled state */ }
    finally { setActing(prev => ({ ...prev, [id]: false })); }
  };

  // ── Row renderer ─────────────────────────────────────────────────────────────

  const renderRow = (f: FriendEntry, variant: 'friend' | 'received' | 'sent') => {
    const displayName = f.character?.name ?? f.email;
    const subLine     = f.character ? f.email : null;
    const icon        = f.character?.class ? (CLASS_ICON[f.character.class] ?? '👤') : '👤';
    const busy        = !!acting[f.friendship_id];

    return (
      <div key={f.friendship_id} className={styles.friendRow}>
        <span className={styles.friendAvatar}>{icon}</span>

        <div className={styles.friendInfo}>
          <span className={styles.friendName}>{displayName}</span>
          {subLine && <span className={styles.friendEmail}>{subLine}</span>}
        </div>

        <div className={styles.friendActions}>
          {variant === 'received' && (
            <>
              <button
                className={styles.btnAccept}
                disabled={busy}
                title={t('friends.accept')}
                onClick={() => act(f.friendship_id, () => friendApi.accept(token, f.friendship_id))}
              >✔</button>
              <button
                className={styles.btnDecline}
                disabled={busy}
                title={t('friends.decline')}
                onClick={() => act(f.friendship_id, () => friendApi.remove(token, f.friendship_id))}
              >✕</button>
            </>
          )}

          {variant === 'sent' && (
            <button
              className={styles.btnDecline}
              disabled={busy}
              title={t('friends.cancel')}
              onClick={() => act(f.friendship_id, () => friendApi.remove(token, f.friendship_id))}
            >✕</button>
          )}

          {variant === 'friend' && (
            <button
              className={styles.btnRemove}
              disabled={busy}
              title={t('friends.remove')}
              onClick={() => act(f.friendship_id, () => friendApi.remove(token, f.friendship_id))}
            >🗑</button>
          )}
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const totalPending = (data?.received?.length ?? 0);

  return (
    <section className={styles.friendsSection}>

      {/* ── Section header ── */}
      <div className={styles.friendsHeader}>
        <h2 className={styles.sectionTitle}>
          👥 {t('friends.title')}
          {totalPending > 0 && (
            <span className={styles.pendingBadge}>{totalPending}</span>
          )}
        </h2>
      </div>

      {/* ── Search / Add ── */}
      <form className={styles.friendSearch} onSubmit={handleSend}>
        <input
          className={`${styles.input} ${styles.friendInput}`}
          type="text"
          placeholder={t('friends.placeholder')}
          value={query}
          onChange={e => { setQuery(e.target.value); setSendMsg(null); }}
          autoComplete="off"
        />
        <button
          type="submit"
          className={styles.btnSend}
          disabled={!query.trim() || sending}
        >
          {sending ? '…' : t('friends.sendReq')}
        </button>
      </form>

      {sendMsg && (
        <p className={sendMsg.type === 'ok' ? styles.sendOk : styles.error}>
          {sendMsg.text}
        </p>
      )}

      {/* ── Content ── */}
      {loading ? (
        <>
          <div className={styles.skeleton} style={{ height: 52 }} />
          <div className={styles.skeleton} style={{ height: 52, opacity: 0.6 }} />
        </>
      ) : (
        <>
          {/* Received requests */}
          {!!data?.received?.length && (
            <div className={styles.friendGroup}>
              <h3 className={styles.groupTitle}>
                {t('friends.received')}
                <span className={styles.badge}>{data.received.length}</span>
              </h3>
              {data.received.map(f => renderRow(f, 'received'))}
            </div>
          )}

          {/* Sent requests */}
          {!!data?.sent?.length && (
            <div className={styles.friendGroup}>
              <h3 className={styles.groupTitle}>{t('friends.sent')}</h3>
              {data.sent.map(f => renderRow(f, 'sent'))}
            </div>
          )}

          {/* Friends list */}
          <div className={styles.friendGroup}>
            {data?.friends?.length ? (
              <>
                <h3 className={styles.groupTitle}>
                  {t('friends.list')}
                  <span className={styles.badge}>{data.friends.length}</span>
                </h3>
                {data.friends.map(f => renderRow(f, 'friend'))}
              </>
            ) : (
              !data?.received?.length && !data?.sent?.length && (
                <p className={styles.emptyFriends}>
                  {t('friends.empty')}
                </p>
              )
            )}
          </div>
        </>
      )}
    </section>
  );
}
